import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import { subscribeToBrief, type BriefRole } from "@/lib/brief";
import { Button, Notice, Panel, SelectField, TextField } from "@/components/system";

const ROLE_OPTIONS: Array<{ value: BriefRole; label: string }> = [
  { value: "gp", label: "GP" },
  { value: "specialist", label: "Specialist" },
  { value: "practice_staff", label: "Practice staff" },
  { value: "other", label: "Other" },
];

/** Weekly brief sign-up: email, role and button on one row (stacked on phones). */
export function BriefSubscribe({
  sourcePath,
}: {
  /** Kept for older call sites; every placement now renders the same panel. */
  variant?: "default" | "footer";
  sourcePath?: string;
}) {
  const { city } = useCity();
  const [searchParams] = useSearchParams();
  const invitedByColleague = searchParams.get("via") === "invite";
  const prefilledEmail = invitedByColleague ? searchParams.get("email")?.trim() ?? "" : "";
  const [email, setEmail] = useState(prefilledEmail);
  const [role, setRole] = useState<BriefRole>("gp");
  const [state, setState] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState("submitting");
    try {
      await subscribeToBrief(city.slug, {
        email: email.trim(),
        role,
        sourcePath: sourcePath ?? `${window.location.pathname}${window.location.search}`,
      });
      track("brief_subscribe");
      setEmail("");
      setState("success");
    } catch {
      setState("error");
    }
  };

  return (
    <Panel aria-labelledby="brief-subscribe-heading" className="ds-brief-subscribe">
      <div className="ds-stack" style={{ gap: 8 }}>
        <p className="ds-eyebrow ds-eyebrow--teal">{city.name} Referral Brief</p>
        <h2 id="brief-subscribe-heading" className="ds-title">Subscribe to the weekly brief</h2>
        <p className="ds-small">
          Availability changes, new listings and current public-health alerts. Tuesday mornings, unsubscribe any time.
        </p>
        {invitedByColleague ? <Notice>A colleague invited you. Confirm your email to subscribe.</Notice> : null}
      </div>

      {state === "success" ? (
        <Notice tone="success">
          <strong>Check your inbox to confirm.</strong> We only add you once you confirm.{" "}
          <button type="button" className="ds-link" style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit" }} onClick={() => setState("idle")}>
            Use another email
          </button>
        </Notice>
      ) : (
        <form onSubmit={handleSubmit} className="ds-stack" style={{ gap: 12 }}>
          <div className="ds-form-row" style={{ ["--ds-form-cols" as string]: "minmax(0, 1fr) minmax(0, 180px) auto" }}>
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@practice.com.au"
            />
            <SelectField label="Role" value={role} onChange={(event) => setRole(event.target.value as BriefRole)}>
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </SelectField>
            <Button type="submit" disabled={state === "submitting" || !email.trim()}>
              {state === "submitting" ? "Subscribing…" : "Subscribe"}
            </Button>
          </div>
          {state === "error" ? <Notice tone="error">We could not start the subscription. Check the email address and try again.</Notice> : null}
          <p className="ds-fine">
            Your email is used only for the brief. See the <Link className="ds-link" to="/privacy">privacy policy</Link>.
          </p>
        </form>
      )}
    </Panel>
  );
}
